import React, { useState, useCallback, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { ApiService } from 'services/ApiService';

// react-easy-crop's index.d.ts only re-exports types via "export type *",
// so TypeScript cannot see Cropper as a JSX component. Cast it explicitly.
const CropperComponent = Cropper as React.ComponentType<any>;

/** Matches react-easy-crop's Area type */
interface CropArea {
  width: number;
  height: number;
  x: number;
  y: number;
}

interface ProfilePictureCropperProps {
  imageUrl: string;
  artistId: string;
  artistName: string;
  onClose: () => void;
  onComplete: () => void;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: CropArea,
): Promise<Blob> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      },
      'image/png',
      1,
    );
  });
};

const ProfilePictureCropper: React.FC<ProfilePictureCropperProps> = ({
  imageUrl,
  artistId,
  artistName,
  onClose,
  onComplete,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const onCropComplete = useCallback(
    (_croppedArea: CropArea, croppedPixels: CropArea) => {
      setCroppedAreaPixels(croppedPixels);
    },
    [],
  );

  // Generate preview blob whenever the crop area changes
  useEffect(() => {
    if (!croppedAreaPixels || croppedAreaPixels.width === 0 || croppedAreaPixels.height === 0) {
      setPreviewUrl(null);
      return;
    }

    let cancelled = false;
    getCroppedImg(imageUrl, croppedAreaPixels).then((blob) => {
      if (cancelled) return;
      // Revoke previous preview URL to avoid memory leaks
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    }).catch(() => {
      if (!cancelled) setPreviewUrl(null);
    });

    return () => {
      cancelled = true;
    };
  }, [imageUrl, croppedAreaPixels]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const handleApply = async () => {
    if (!croppedAreaPixels || croppedAreaPixels.width === 0) return;
    setUploading(true);
    try {
      // Step 1: Crop the image client-side
      const croppedBlob = await getCroppedImg(imageUrl, croppedAreaPixels);

      // Step 2: Upload the cropped image as a new ArtistImage
      const formData = new FormData();
      formData.append('file', croppedBlob, 'profile-picture.png');
      formData.append('name', `${artistName} Profile Picture`);
      formData.append('description', 'Profile picture');

      const uploadResponse = await ApiService.uploadArtistImage(artistId, formData);
      if (!uploadResponse.success || !uploadResponse.data) {
        throw new Error('Failed to upload cropped image');
      }

      const newImageId = uploadResponse.data.id;

      // Step 3: Set the new image as the profile picture
      await ApiService.setImageAsProfilePicture(artistId, newImageId);

      onComplete();
    } catch (error) {
      console.error('Error setting profile picture:', error);
      alert('Failed to set profile picture. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Crop Profile Picture
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            disabled={uploading}
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body: Cropper + Preview side panel */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0">
          {/* Cropper */}
          <div className="relative flex-1 min-h-[320px] md:min-h-0 bg-gray-200">
            <CropperComponent
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          {/* Preview sidebar */}
          <div className="w-full md:w-80 px-5 py-4 bg-gray-50 border-l border-gray-200 overflow-y-auto">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Preview
            </h3>

            {/* Large circular avatar preview */}
            <div className="flex flex-col items-center mb-6">
              <p className="text-xs text-gray-500 mb-2">Profile Avatar</p>
              <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-white shadow-md bg-gray-200">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Cropped preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-3xl text-gray-300">?</span>
                  </div>
                )}
              </div>
            </div>

            {/* Small avatar in dashboard context */}
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 mb-6 shadow-sm">
              <p className="text-xs text-gray-500 mb-3">In Dashboard</p>
              <div className="space-y-3">
                {/* Project list row */}
                <div className="flex items-center gap-2">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Avatar"
                      className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <span className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center flex-shrink-0 text-[10px] font-semibold">
                      {artistName.charAt(0).toUpperCase() || '?'}
                    </span>
                  )}
                  <span className="text-sm text-gray-700 truncate">{artistName}</span>
                </div>

                {/* Team member row */}
                <div className="flex items-center gap-2 border-t border-gray-100 pt-2">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Avatar"
                      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <span className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-semibold">
                      {artistName.charAt(0).toUpperCase() || '?'}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {artistName}
                    </p>
                    <p className="text-xs text-gray-500">Team member</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Zoom slider */}
            <div>
              <label
                htmlFor="zoom-slider"
                className="text-xs text-gray-500 mb-1 block"
              >
                Zoom: {Math.round(zoom * 100)}%
              </label>
              <input
                id="zoom-slider"
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full accent-purple-600"
              />
              <p className="text-xs text-gray-400 mt-2">
                Drag the image to position. Use the slider or scroll to zoom.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-white rounded-b-xl">
          <button
            onClick={onClose}
            disabled={uploading}
            className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={uploading || !croppedAreaPixels || croppedAreaPixels.width === 0}
            className="px-5 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors inline-flex items-center gap-2"
          >
            {uploading && (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {uploading ? 'Applying...' : 'Set as Profile Picture'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePictureCropper;
